import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ShareIcon from '@mui/icons-material/Share';
import { QRCodeCanvas } from 'qrcode.react';
import { buildSurveyPublicUrl } from './SurveyShareDialog';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * "View survey link" modal: public URL + QR code with copy and share.
 * Share collects a list of email addresses (delivery is wired up later).
 */
export default function SurveyLinkDialog({
  open,
  onClose,
  surveyTitle,
  publicToken,
  onCopySuccess,
  onCopyError,
}) {
  const [shareOpen, setShareOpen] = useState(false);
  const [emails, setEmails] = useState([]);
  const [emailError, setEmailError] = useState('');
  const [shareSaved, setShareSaved] = useState(false);

  useEffect(() => {
    if (!open) return;
    setShareOpen(false);
    setEmails([]);
    setEmailError('');
    setShareSaved(false);
  }, [open]);

  const url = useMemo(
    () => (publicToken ? buildSurveyPublicUrl(publicToken) : ''),
    [publicToken],
  );

  function copyUrl() {
    void navigator.clipboard
      .writeText(url)
      .then(() => onCopySuccess?.())
      .catch(() => onCopyError?.());
  }

  function handleEmailsChange(next) {
    const invalid = next.find((e) => !EMAIL_RE.test(e));
    if (invalid) {
      setEmailError(`"${invalid}" is not a valid email address.`);
      return;
    }
    setEmailError('');
    setShareSaved(false);
    setEmails([...new Set(next.map((e) => e.trim().toLowerCase()))]);
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>Survey link</DialogTitle>
      <DialogContent>
        {surveyTitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Public link for <strong>{surveyTitle}</strong> — anyone with the link (or QR scan) can
            respond while the survey is open.
          </Typography>
        )}

        {/* URL + copy */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '8px',
            px: 1.25,
            py: 0.75,
            backgroundColor: '#fafafa',
          }}
        >
          <Typography
            component="code"
            variant="body2"
            sx={{ flex: 1, overflowWrap: 'anywhere', fontFamily: 'monospace', fontSize: '0.8rem' }}
          >
            {url}
          </Typography>
          <Tooltip title="Copy link">
            <IconButton size="small" onClick={copyUrl} aria-label="Copy survey link">
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* QR code */}
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
          <Box
            sx={{
              p: 1.5,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '12px',
              backgroundColor: '#ffffff',
            }}
            aria-label="QR code for survey link"
          >
            <QRCodeCanvas value={url} size={180} includeMargin={false} />
          </Box>
        </Box>

        {/* Share by email */}
        {!shareOpen ? (
          <Button
            startIcon={<ShareIcon fontSize="small" />}
            onClick={() => setShareOpen(true)}
            sx={{ textTransform: 'none' }}
          >
            Share
          </Button>
        ) : (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
              Share with people
            </Typography>
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={emails}
              onChange={(e, next) => handleEmailsChange(next)}
              renderTags={(selected, getTagProps) =>
                selected.map((option, index) => (
                  <Chip size="small" label={option} {...getTagProps({ index })} key={option} />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="Type an email and press Enter…"
                  error={Boolean(emailError)}
                  helperText={emailError || 'Add one or more email addresses to share this survey with.'}
                />
              )}
            />
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                variant="contained"
                size="small"
                disabled={emails.length === 0}
                onClick={() => setShareSaved(true)}
                sx={{
                  textTransform: 'none',
                  backgroundColor: '#000000',
                  '&:hover': { backgroundColor: '#333333' },
                }}
              >
                Share with {emails.length || ''} {emails.length === 1 ? 'person' : 'people'}
              </Button>
              {shareSaved && (
                <Typography variant="caption" color="success.main">
                  Recipient list saved — email delivery is coming soon.
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
